import { useEffect, useRef } from "react";
import { Button } from "../ui/button";
import { RefreshCw } from "lucide-react";
import { StepHeader, NavButtons, AiLoader } from "../common";
import { useSession, buildCase } from "../../lib/session";
import { QuestionRow, TentativeView, NeedsHistoryNotice } from "./followup/FollowupParts";

const loaderText = (pending) => {
  if (pending.followup) return "Generating focused & negative history questions";
  if (pending.tentative) return "Forming tentative differential diagnosis";
  return "";
};

export default function FollowupStep() {
  const { state, set, run, pending } = useSession();
  const { questions, answers } = state.followup;
  const hasComplaints = !!state.history.chief_complaints?.trim();
  const loading = loaderText(pending);

  const loadQuestions = () => run("followup", buildCase(state, null, "followup"), (r) => {
    set("followup", { questions: r.questions || [], answers: {}, correlation: r.textbook_correlation });
    set("tentative", null);
  });
  const getDdx = () => run("tentative", buildCase(state, null, "tentative"), (r) => set("tentative", r));
  const ans = (id, v) => set("followup.answers", { ...answers, [id]: v });

  // Auto-generate once on entering the step; ref holds the latest closure so the effect stays mount-only.
  const autoLoad = useRef(null);
  autoLoad.current = () => { if (!questions.length && hasComplaints) loadQuestions(); };
  useEffect(() => { autoLoad.current(); }, []);

  return (
    <div>
      <StepHeader num={2} title="Focused History & Tentative DDx" sub="AI-generated questions about the chief complaints, including pertinent negatives." />
      {!hasComplaints && <NeedsHistoryNotice />}
      {loading && <AiLoader text={loading} />}
      {!loading && questions.length > 0 && (
        <div className="border rounded-md bg-card divide-y" data-testid="followup-questions">
          {questions.map((q, i) => <QuestionRow key={q.id} q={q} i={i} value={answers[q.id]} onAnswer={(v) => ans(q.id, v)} />)}
        </div>
      )}
      {!loading && (
        <div className="flex flex-wrap gap-3 mt-6">
          <Button data-testid="btn-get-tentative-ddx" className="cc-btn" onClick={getDdx} disabled={!questions.length}>Generate tentative DDx</Button>
          <Button variant="outline" data-testid="btn-regenerate-questions" onClick={loadQuestions} disabled={!hasComplaints}><RefreshCw className="w-4 h-4 mr-2" />Regenerate questions</Button>
        </div>
      )}
      {state.tentative && !loading && <TentativeView t={state.tentative} />}
      <NavButtons onBack={() => set("step", 0)} onNext={() => set("step", 2)} nextLabel="Vitals & triage" />
    </div>
  );
}
